/**
 * Raster planning + debounce scheduling for the station-labels overlay
 * (map-FPS perf Stage 3W).
 *
 * The 7.5 MB ~7000-path `texts-*.svg` used to be displayed as one `<img>`
 * whose CSS scale changed every zoom frame — the browser re-rasterized the
 * whole SVG document per frame (the user-visible zoom stutter). The fix:
 * the overlay displays a CACHED raster (a `<canvas>` drawn once from the
 * SVG image) and, during a zoom gesture, only compositor-scales it
 * (transient blur accepted). This module decides WHEN to re-raster and at
 * WHAT resolution/region; the overlay component owns the actual
 * `drawImage`.
 *
 * Scheduling contract (plan Task 3W.1): zero re-rasters while the zoom
 * keeps changing; exactly one re-raster once it settles (trailing
 * debounce, also coalescing wheel bursts). Pan stays translate-only and
 * never rasters — except at extreme zoom, where the raster covers only a
 * viewport WINDOW (pixel-budget guard) and a pan that escapes the window
 * re-rasters after the same debounce.
 */

/** Canvas edge in canvas units — the SVG viewBox is 0 0 4096 4096. */
const CANVAS_EDGE = 4096
/**
 * Raster-target pixel budget: one full canvas at zoom 1 (4096² ≈ 16.8 M px
 * ≈ 64 MB RGBA). Above it the plan windows the region and/or caps the
 * scale, trading crispness at extreme zoom for bounded memory.
 */
export const MAX_RASTER_PIXELS = CANVAS_EDGE * CANVAS_EDGE
/** Trailing debounce before the settle re-raster (also coalesces wheel bursts). */
export const RASTER_DEBOUNCE_MS = 150
/**
 * The window spans this multiple of the visible rect (centred on it), so
 * a moderate pan at extreme zoom stays inside the cached raster.
 */
const WINDOW_VIEWPORT_FACTOR = 2

/** Per-frame camera + viewport state, as the overlay's apply() sees it. */
export interface LabelRasterInput {
  /** Physical px per canvas unit. */
  readonly zoom: number
  /** Unscaled canvas-space pan (world point at the viewport origin). */
  readonly offsetX: number
  readonly offsetY: number
  readonly dpr: number
  /** Physical viewport size in device pixels. */
  readonly viewportPxW: number
  readonly viewportPxH: number
}

/** Canvas-units rectangle of the SVG to rasterize. */
export interface LabelRasterRegion {
  readonly x: number
  readonly y: number
  readonly w: number
  readonly h: number
}

/** One raster order: which region to draw, at which resolution. */
export interface LabelRasterPlan {
  readonly region: LabelRasterRegion
  /** Physical px per canvas unit of the raster target (≤ zoom when capped). */
  readonly scale: number
  readonly pixelW: number
  readonly pixelH: number
  /** The zoom the plan was made for — the scheduler's identity key. */
  readonly zoom: number
}

/**
 * Computes the raster region + resolution for the given camera state.
 * Full canvas at native resolution while it fits {@link MAX_RASTER_PIXELS};
 * otherwise a {@link WINDOW_VIEWPORT_FACTOR}× viewport window, with the
 * scale capped so the target never exceeds the budget. `null` for
 * degenerate input (pre-init zoom / empty viewport).
 */
export function planLabelRaster(input: LabelRasterInput): LabelRasterPlan | null {
  const { zoom, offsetX, offsetY, viewportPxW, viewportPxH } = input
  // Positive-and-finite guards (NOT `<= 0` — a NaN slips through every
  // comparison): a degenerate or non-finite camera must never produce a
  // NaN-sized plan, which would zero the raster canvas. Fail-safe: no
  // plan → the overlay keeps showing the previous raster.
  if (!Number.isFinite(zoom) || zoom <= 0) return null
  if (!Number.isFinite(viewportPxW) || viewportPxW <= 0) return null
  if (!Number.isFinite(viewportPxH) || viewportPxH <= 0) return null

  const fullEdgePx = CANVAS_EDGE * zoom
  let region: LabelRasterRegion
  let scale: number
  if (fullEdgePx * fullEdgePx <= MAX_RASTER_PIXELS) {
    region = { x: 0, y: 0, w: CANVAS_EDGE, h: CANVAS_EDGE }
    scale = zoom
  } else {
    const visW = viewportPxW / zoom
    const visH = viewportPxH / zoom
    const w = Math.min(CANVAS_EDGE, visW * WINDOW_VIEWPORT_FACTOR)
    const h = Math.min(CANVAS_EDGE, visH * WINDOW_VIEWPORT_FACTOR)
    const x = clamp(offsetX - (w - visW) / 2, 0, CANVAS_EDGE - w)
    const y = clamp(offsetY - (h - visH) / 2, 0, CANVAS_EDGE - h)
    region = { x, y, w, h }
    scale = Math.min(zoom, Math.sqrt(MAX_RASTER_PIXELS / (w * h)))
  }
  return {
    region,
    scale,
    pixelW: Math.ceil(region.w * scale),
    pixelH: Math.ceil(region.h * scale),
    zoom,
  }
}

export interface LabelRasterScheduler {
  /**
   * Per-frame camera feed (call from apply()). Schedules a trailing
   * debounced re-raster when the state diverges from the last raster;
   * cancels a pending one when it converges back.
   */
  observe(input: LabelRasterInput): void
  /** Raster immediately (initial load / source swap) — no debounce. */
  rasterNow(input: LabelRasterInput): void
  /** Forget the recorded raster and drop any pending timer (source swap). */
  reset(): void
}

export function createLabelRasterScheduler(options: {
  onRaster: (plan: LabelRasterPlan) => void
  debounceMs?: number
}): LabelRasterScheduler {
  const debounceMs = options.debounceMs ?? RASTER_DEBOUNCE_MS
  let lastRaster: LabelRasterPlan | null = null
  let latestInput: LabelRasterInput | null = null
  let timer: ReturnType<typeof setTimeout> | null = null

  const cancelPending = (): void => {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
  }

  const fire = (input: LabelRasterInput): void => {
    const plan = planLabelRaster(input)
    if (plan === null) return
    lastRaster = plan
    options.onRaster(plan)
  }

  const satisfiedBy = (input: LabelRasterInput): boolean => {
    if (lastRaster === null) return false
    if (input.zoom !== lastRaster.zoom) return false
    // Visible rect (clamped into the canvas — the camera can legally sit
    // past the edge) must be inside the rastered region.
    const visW = input.viewportPxW / input.zoom
    const visH = input.viewportPxH / input.zoom
    const visX0 = clamp(input.offsetX, 0, CANVAS_EDGE)
    const visY0 = clamp(input.offsetY, 0, CANVAS_EDGE)
    const visX1 = clamp(input.offsetX + visW, 0, CANVAS_EDGE)
    const visY1 = clamp(input.offsetY + visH, 0, CANVAS_EDGE)
    const { region } = lastRaster
    return (
      visX0 >= region.x &&
      visY0 >= region.y &&
      visX1 <= region.x + region.w &&
      visY1 <= region.y + region.h
    )
  }

  return {
    observe(input: LabelRasterInput): void {
      latestInput = input
      if (satisfiedBy(input)) {
        cancelPending()
        return
      }
      cancelPending()
      timer = setTimeout(() => {
        timer = null
        if (latestInput !== null) fire(latestInput)
      }, debounceMs)
    },
    rasterNow(input: LabelRasterInput): void {
      latestInput = input
      cancelPending()
      fire(input)
    },
    reset(): void {
      cancelPending()
      lastRaster = null
    },
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value))
}
